import React, { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, Flag, Pencil, Sparkles } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Task } from './types';

interface TaskCardProps {
  task: Task;
  index: number;
  onEdit: (task: Task) => void;
  isNewTask?: boolean;
  showSparkles?: boolean;
  isDragging?: boolean;
  isOverlay?: boolean;
}

const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'medium':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'low':
      return 'bg-green-50 text-green-700 border-green-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const TaskCardComponent: React.FC<TaskCardProps> = ({ 
  task, 
  index, 
  onEdit, 
  isNewTask, 
  showSparkles,
  isDragging = false,
  isOverlay = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: task.id.toString(),
    disabled: isOverlay,
  });

  // Log when this task is being highlighted
  if (isNewTask || showSparkles) {
    console.log('🌟 TaskCard rendering with highlighting:', {
      taskId: task.id,
      taskName: task.name,
      isNewTask,
      showSparkles,
      timestamp: new Date().toISOString()
    });
  }



  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  // Don't render the original when dragging (DragOverlay will show it)
  if (isSortableDragging && !isOverlay) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="opacity-30 bg-gray-100 rounded-xl border border-dashed border-gray-300 h-20 mb-3"
      />
    );
  }

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      {...(isOverlay ? {} : attributes)}
      {...(isOverlay ? {} : listeners)}
      data-task-id={task.id}
            onDragStart={(e) => {
        e.preventDefault(); // Prevent native HTML5 drag
      }}
      className={`
        task-card group bg-white rounded-xl shadow-sm border mb-3 select-none relative cursor-grab
        transition-all duration-200 ease-out
        ${isNewTask 
          ? 'border-pink-300 shadow-lg ring-2 ring-pink-500/20 bg-gradient-to-r from-pink-50/50 to-rose-50/50' 
          : 'border-gray-200'
        }
        ${isDragging || isSortableDragging
          ? 'shadow-2xl ring-2 ring-blue-500/30 transform scale-105 rotate-1 cursor-grabbing z-50' 
          : 'hover:shadow-md hover:border-gray-300'
        }
        ${isOverlay ? 'pointer-events-none' : 'pointer-events-auto'}
      `}
      style={{
        ...(isOverlay ? {} : style),
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      <div className="p-4 relative">
        {/* Sparkles Effect */}
        <AnimatePresence>
          {showSparkles && (
            <>
              {/* Main sparkle with rotation */}
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: 0 }}
                animate={{ 
                  opacity: 1, 
                  scale: [1, 1.2, 1], 
                  rotate: [0, 180, 360]
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ 
                  duration: 0.6,
                  rotate: { duration: 2, repeat: Infinity, ease: "linear" }
                }}
                className="absolute -top-1 -right-1 text-pink-500 z-20"
              >
                <Sparkles className="w-4 h-4 drop-shadow-sm" />
              </motion.div>
              
              {/* Secondary sparkle */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0, 1],
                  scale: [0, 1, 1.1, 1]
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.2,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
                className="absolute top-1 -left-1 text-pink-400 z-20"
              >
                <Sparkles className="w-3 h-3 drop-shadow-sm" />
              </motion.div>
              
              {/* Third sparkle */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0, 1, 1.2, 0]
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ 
                  duration: 1, 
                  delay: 0.4,
                  repeat: Infinity,
                  repeatDelay: 1.5
                }}
                className="absolute bottom-1 right-2 text-rose-400 z-20"
              >
                <Sparkles className="w-3 h-3 drop-shadow-sm" />
              </motion.div>
              
              {/* Pulsing background glow */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: [0, 0.3, 0, 0.3, 0]
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 bg-gradient-to-r from-pink-100/50 to-rose-100/50 rounded-xl pointer-events-none"
              />
            </>
          )}
        </AnimatePresence>
        
        {!isOverlay && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit(task);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 transition-colors duration-200 rounded-lg hover:bg-gray-100 z-10 pointer-events-auto"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
        
        <h4 className={`mb-2 text-sm pr-8 leading-relaxed transition-colors duration-200 ${
          isNewTask 
            ? 'font-bold text-gray-900' 
            : 'font-semibold text-gray-900 group-hover:text-blue-700'
        }`}>
          {task.name}
        </h4>
        
        {task.description && (
          <p className="text-xs text-gray-600 mb-3 leading-relaxed">{task.description}</p>
        )}
        
        <div className="flex items-center justify-between text-xs mt-3">
          {task.dueDate && (
            <div className="flex items-center text-gray-500 bg-gray-50 px-2 py-1 rounded-lg">
              <Calendar className="w-3 h-3 mr-1.5" />
              <span className="font-medium">{task.dueDate}</span>
            </div>
          )}
          <div className={`px-2 py-1 rounded-lg text-xs font-medium border ${getPriorityColor(task.priority)}`}>
            <Flag className="w-3 h-3 inline mr-1" />
            {task.priority}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TaskCard = memo(TaskCardComponent);
